import {
  ColumnDef,
  SortingState,
  getSortedRowModel,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  Row,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useState } from "react";
import { cn } from "@/utils/utils";

const defaultIsErrorRow = () => false;

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  onClick?: (row: Row<TData>) => void;
  data: TData[];
  className?: string;

  // specify true if you want to dump all rows without pagination
  noPagination?: boolean;
  isErrorRow?: (rowData: any) => boolean;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  onClick,
  className,
  noPagination,
  isErrorRow = defaultIsErrorRow,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);

  // show up to 1000 rows if pagination is disabled
  const noPaginationDefaultParam = {
    pageSize: 1000,
    pageIndex: 0,
  };

  // TODO: Increase page size to Infinite if no pagiation is specified
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: noPagination ? getPaginationRowModel() : undefined,
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row: any) => row.id,
    state: {
      sorting,
      pagination: noPagination ? noPaginationDefaultParam : undefined,
    },
  });

  const innerPadding = "0.75rem";

  return (
    <Table className={className}>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              return (
                <TableHead
                  key={header.id}
                  style={{
                    paddingLeft: innerPadding,
                    paddingRight: innerPadding,
                  }}
                >
                  <div className="text-xs font-[500] tracking-[1.2px]">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </div>
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() && "selected"}
              onClick={() => {
                if (onClick) {
                  onClick(row);
                }
              }}
              className={cn({
                "bg-error/20 hover:bg-error/25": isErrorRow(row.original),
                "cursor-pointer": !!onClick,
              })}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  className={cn("text-base", {
                    "first:rounded-bl-sm first:rounded-tl-sm last:rounded-br-sm last:rounded-tr-sm":
                      isErrorRow(row.original),
                  })}
                  style={{
                    paddingLeft: innerPadding,
                    paddingRight: innerPadding,
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={columns.length} className="h-24 text-center">
              No results.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
